import { useState, useEffect } from "react";
import { X, Loader2, Send, Mail } from "lucide-react";
import { toast } from "react-hot-toast";

type ReportType = "minutes_usage" | "ai_minutes_usage" | "capture_rates" | "kpi_summary" | "pcc_performance";
type Scope      = "customer" | "region";
type Interval   = "daily" | "hourly";

type KpiFieldKey =
  | "capture_rate"
  | "total_calls"
  | "calls_answered"
  | "missed_calls"
  | "avg_talk_time";

const KPI_FIELDS: { key: KpiFieldKey; label: string }[] = [
  { key: "capture_rate",   label: "Capture Rate" },
  { key: "total_calls",    label: "Total Calls" },
  { key: "calls_answered", label: "Calls Answered" },
  { key: "missed_calls",   label: "Missed Calls" },
  { key: "avg_talk_time",  label: "Average Talk Time" },
];

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  minutes_usage:    "Minutes Usage",
  ai_minutes_usage: "AI Minutes Usage",
  capture_rates:    "Capture Rates",
  kpi_summary:      "KPI Summary",
  pcc_performance:  "PCC Performance",
};

export interface SendEmailReportCustomer {
  id: string;
  name: string;
}

export interface SendEmailReportRegion {
  id: string;
  name: string;
}

interface SendEmailReportModalProps {
  reportType: ReportType;
  availableCustomers: SendEmailReportCustomer[];
  /** Pre-select a specific customer (e.g. when one is already selected in the page filter) */
  defaultCustomerId?: string;
  defaultStartDate: string;  // yyyy-MM-dd
  defaultEndDate: string;    // yyyy-MM-dd
  minDurationSeconds?: number;
  onClose: () => void;
}

/** Parse a comma/semicolon-separated email string into a validated array */
function parseRecipients(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function SendEmailReportModal({
  reportType,
  availableCustomers,
  defaultCustomerId,
  defaultStartDate,
  defaultEndDate,
  minDurationSeconds,
  onClose,
}: SendEmailReportModalProps) {
  const [scope, setScope]           = useState<Scope>("customer");
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? "");
  const [regionId, setRegionId]     = useState("");
  const [regions, setRegions]       = useState<SendEmailReportRegion[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [interval, setInterval]     = useState<Interval>("daily");
  const [recipientsRaw, setRecipientsRaw] = useState("");
  const [startDate, setStartDate]   = useState(defaultStartDate);
  const [endDate, setEndDate]       = useState(defaultEndDate);
  const [includeAttachment, setIncludeAttachment] = useState(true);
  const [kpiFields, setKpiFields]   = useState<KpiFieldKey[]>([
    "capture_rate",
    "total_calls",
    "calls_answered",
    "missed_calls",
  ]);
  const [sending, setSending] = useState(false);

  // Load regions when modal opens (only needed for capture_rates scope selector)
  useEffect(() => {
    if (reportType !== "capture_rates") return;
    setRegionsLoading(true);
    fetch("/api/v1/capture-rates/regions-list", {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setRegions(data.data ?? []);
      })
      .catch(() => { /* silent — region scope just won't be available */ })
      .finally(() => setRegionsLoading(false));
  }, [reportType]);

  const toggleKpiField = (key: KpiFieldKey) => {
    setKpiFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSend = async () => {
    const recipients = parseRecipients(recipientsRaw);

    // Validate scope-specific selection (pcc_performance needs neither customer nor region)
    if (reportType === "capture_rates" && scope === "customer" && !customerId) {
      toast.error("Please select a customer");
      return;
    }
    if (reportType === "capture_rates" && scope === "region" && !regionId) {
      toast.error("Please select a region");
      return;
    }
    // "all" is valid for both dropdowns — no further check needed
    if (reportType !== "capture_rates" && reportType !== "pcc_performance" && (!customerId || customerId === "all")) {
      toast.error("Please select a specific customer");
      return;
    }

    if (recipients.length === 0) {
      toast.error("Enter at least one recipient email");
      return;
    }
    const invalid = recipients.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      toast.error(`Invalid email address: ${invalid[0]}`);
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please select a date range");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }
    if (reportType === "kpi_summary" && kpiFields.length === 0) {
      toast.error("Select at least one KPI field");
      return;
    }

    setSending(true);
    try {
      const body: Record<string, unknown> = {
        reportType,
        recipients,
        startDate,
        endDate,
      };

      if (reportType === "capture_rates") {
        body.scope = scope;
        body.interval = interval;
        if (scope === "customer") {
          if (customerId && customerId !== "all") body.customerProfileId = customerId;
        } else {
          if (regionId && regionId !== "all") body.regionId = regionId;
        }
        if (includeAttachment) body.includeAttachment = true;
      } else if (reportType === "pcc_performance") {
        body.includeAttachment = includeAttachment;
      } else {
        body.customerProfileId = customerId;
        if (reportType === "minutes_usage" || reportType === "ai_minutes_usage") body.includeAttachment = includeAttachment;
        if (reportType === "kpi_summary")   body.kpiFields = kpiFields;
      }

      if (minDurationSeconds !== undefined && minDurationSeconds > 0) {
        body.minDurationSeconds = minDurationSeconds;
      }

      const res = await fetch("/api/v1/email-reports/send-now", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to send report");
      }

      toast.success(`Report sent to ${recipients.join(", ")}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Send {REPORT_TYPE_LABELS[reportType]} Report
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">

          {/* Scope selector — Capture Rates only */}
          {reportType === "capture_rates" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Scope
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setScope("customer")}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    scope === "customer"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Customer
                </button>
                <button
                  type="button"
                  onClick={() => setScope("region")}
                  disabled={regionsLoading || regions.length === 0}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40 ${
                    scope === "region"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Region
                </button>
              </div>
            </div>
          )}

          {/* Customer dropdown — not needed for pcc_performance */}
          {reportType !== "pcc_performance" && (reportType !== "capture_rates" || scope === "customer") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select a customer —</option>
                {reportType === "capture_rates" && (
                  <option value="all">All Customers</option>
                )}
                {availableCustomers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Region dropdown — Capture Rates region scope */}
          {reportType === "capture_rates" && scope === "region" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region <span className="text-red-500">*</span>
              </label>
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select a region —</option>
                <option value="all">All Regions</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipients <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={recipientsRaw}
              onChange={(e) => setRecipientsRaw(e.target.value)}
              placeholder="client@example.com, team@nr.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Separate multiple addresses with commas
            </p>
          </div>

          {/* XLSX attachment toggle — Minutes Usage, Capture Rates & PCC Performance */}
          {(reportType === "minutes_usage" || reportType === "ai_minutes_usage" || reportType === "capture_rates" || reportType === "pcc_performance") && (
            <div className="flex items-center gap-3">
              <input
                id="includeAttachment"
                type="checkbox"
                checked={includeAttachment}
                onChange={(e) => setIncludeAttachment(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeAttachment" className="text-sm text-gray-700">
                Include XLSX attachment
              </label>
            </div>
          )}

          {/* Interval selector — Capture Rates with attachment */}
          {reportType === "capture_rates" && includeAttachment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                XLSX Interval
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInterval("daily")}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    interval === "daily"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Daily
                </button>
                <button
                  type="button"
                  onClick={() => setInterval("hourly")}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    interval === "hourly"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Hourly
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Queues shown as rows with metrics per {interval === "daily" ? "day" : "hour"}
              </p>
            </div>
          )}

          {/* KPI Summary — field selection */}
          {reportType === "kpi_summary" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                KPI Fields <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {KPI_FIELDS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={kpiFields.includes(key)}
                      onChange={() => toggleKpiField(key)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={sending}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sending ? "Sending…" : "Send Report"}
          </button>
        </div>

      </div>
    </div>
  );
}
